import type {
  CreateOrderRequest,
  OrderResponse,
  UpdateOrderRequest,
} from '@palaccount/contracts';
import { MAX_PICKER_IDS, PICKER_PAGE_SIZE, pickerIds } from '@palaccount/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';

import { api } from '@/api';
import type { TypeaheadOption } from '@/components/ui/typeahead';
import { useDebounced, usePickerState, type PickerState } from '@/lib/typeahead';
import { throwApiError } from '@/lib/server-messages';

import { INVOICES_KEY } from './api';
import { ORDERS_KEY } from './quotes-api';

/** SAL-02 data layer. */

export interface OrderListFilters {
  partnerId?: string | undefined;
  status?: OrderResponse['status'] | undefined;
  /** BR-SAL-03's two statuses, as a server predicate. */
  deliverable?: boolean | undefined;
  /** GEN-25/GEN-50: the printed code or the customer's name, from the list's search box. */
  q?: string | undefined;
}

function orderQuery(filters: OrderListFilters) {
  return {
    ...(filters.partnerId ? { partnerId: filters.partnerId } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.q ? { q: filters.q } : {}),
    ...(filters.deliverable ? { deliverable: 'true' as const } : {}),
  };
}

/** SAL-02's register SCREEN — one page at a time (NFR-42). */
export function usePagedOrders(filters: OrderListFilters, page: number, pageSize: number) {
  return useQuery({
    queryKey: [...ORDERS_KEY, 'page', filters, page, pageSize],
    queryFn: async () => {
      const response = await api.salesOrders.list({
        query: { ...orderQuery(filters), page, pageSize },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * The export's page read — the same query the screen shows, imperative so
 * `fetchAllPages` can walk it (NFR-32; never the unbounded opt-out).
 */
export async function fetchOrdersPage(
  filters: OrderListFilters,
  page: number,
  pageSize: number,
): Promise<{ rows: OrderResponse[]; total: number }> {
  const response = await api.salesOrders.list({ query: { ...orderQuery(filters), page, pageSize } });
  if (response.status !== 200) throwApiError(response.status, response.body);
  return { rows: response.body.orders, total: response.body.total };
}

/** SAL-02 §5a's three tiles, over the whole register (PAGINATION.md §3 step 10). */
export function useOrderStats() {
  return useQuery({
    queryKey: [...ORDERS_KEY, 'stats'],
    queryFn: async () => {
      const response = await api.salesOrders.stats();
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

/** GEN-50 — the delivery note's "which order?" picker: matches, then held. */
export function useOrderSearch(q: string, filters: OrderListFilters = {}) {
  return useQuery({
    queryKey: [...ORDERS_KEY, 'search', q, filters],
    queryFn: async (): Promise<OrderResponse[]> => {
      const response = await api.salesOrders.list({
        query: { ...orderQuery(filters), ...(q ? { q } : {}), pageSize: PICKER_PAGE_SIZE },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.orders;
    },
    placeholderData: keepPreviousData,
  });
}

export function useOrdersHeld(ids: readonly string[]) {
  const key = [...new Set(ids)].sort();
  return useQuery({
    queryKey: [...ORDERS_KEY, 'held', key],
    enabled: key.length > 0,
    queryFn: async (): Promise<OrderResponse[]> => {
      const response = await api.salesOrders.list({
        query: { ids: pickerIds(key), pageSize: MAX_PICKER_IDS },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.orders;
    },
  });
}

/**
 * A document picker's label is the pair a reader recognises — the printed
 * code and who it is with — and the server searches BOTH halves for exactly
 * that reason (`documentLabelSearch`).
 */
export function orderOption(order: OrderResponse): TypeaheadOption {
  return { id: order.id, label: `${order.code} — ${order.partnerName}` };
}

/** SAL-03's "which order?" typeahead, wired: search + the one it holds. */
export function useOrderPicker(
  heldId: string | null | undefined,
  filters: OrderListFilters = {},
): PickerState<OrderResponse> {
  const [query, setQuery] = React.useState('');
  const search = useDebounced(query);
  const ids = React.useMemo(() => (heldId ? [heldId] : []), [heldId]);
  const matches = useOrderSearch(search, filters);
  const held = useOrdersHeld(ids);
  return usePickerState<OrderResponse>({
    query,
    setQuery,
    search,
    matches: matches.data,
    held: held.data,
    toOption: orderOption,
    loading: matches.isFetching,
  });
}

export function useOrder(orderId: string | undefined) {
  return useQuery({
    queryKey: [...ORDERS_KEY, 'one', orderId],
    enabled: Boolean(orderId),
    queryFn: async (): Promise<OrderResponse> => {
      const response = await api.salesOrders.get({ params: { orderId: orderId! } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useCreateOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateOrderRequest): Promise<OrderResponse> => {
      const response = await api.salesOrders.create({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ORDERS_KEY }),
  });
}

export function useUpdateOrder(orderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateOrderRequest): Promise<OrderResponse> => {
      const response = await api.salesOrders.update({ params: { orderId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ORDERS_KEY }),
  });
}

export function useDeleteOrder(orderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<void> => {
      const response = await api.salesOrders.remove({ params: { orderId } });
      if (response.status !== 204) throwApiError(response.status, response.body);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ORDERS_KEY }),
  });
}

/**
 * Confirm — sales.approve (16.3 "for confirming"): the customer is committed.
 * `continueOverLimit` is BR-MD-01-10's Warn-only "Continue?" answered yes:
 * the first call goes without it, and a 409 whose body carries
 * `overCreditLimit: true` is the server asking the question.
 */
export function useConfirmOrder(orderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input?: { continueOverLimit?: boolean }): Promise<OrderResponse> => {
      const response = await api.salesOrders.confirm({
        params: { orderId },
        body: input?.continueOverLimit === true ? { continueOverLimit: true } : {},
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ORDERS_KEY }),
  });
}

/** Cancel order (BR-SAL-02-07): terminal, and the tiles stop counting it. */
export function useCancelOrder(orderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    /** GEN-59: the cancel carries its typed reason. */
    mutationFn: async (reason: string): Promise<OrderResponse> => {
      const response = await api.salesOrders.cancel({ params: { orderId }, body: { reason } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ORDERS_KEY }),
  });
}

/**
 * Wave 4B — Amend after confirmation (sales.approve): Confirmed → Draft with
 * a typed reason; the server refuses, naming the document, while a delivery
 * note or invoice stands against the order.
 */
export function useReopenOrder(orderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (reason: string): Promise<OrderResponse> => {
      const response = await api.salesOrders.reopen({ params: { orderId }, body: { reason } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ORDERS_KEY }),
  });
}

/** Create invoice — links back so the pill can open it (BR-SAL-02-08). */
export function useCreateOrderInvoice(orderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<{ order: OrderResponse; invoiceId: string }> => {
      const response = await api.salesOrders.createInvoice({ params: { orderId }, body: {} });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ORDERS_KEY });
      void queryClient.invalidateQueries({ queryKey: INVOICES_KEY });
    },
  });
}
